// =============================================================================
// TestRome — canonical API contract (shared by backend DTOs, frontend, SDK).
// JSON is camelCase, enums are strings, dates are ISO-8601 strings (UTC).
// Ids are GUID strings. Keep this file and backend/src/TestRome.Application/Contracts in sync.
// =============================================================================

export type Guid = string;
export type IsoDate = string;

// ----------------------------------------------------------------------------- enums
export type OrgRole = 'Owner' | 'Admin' | 'Reviewer';
export type Platform = 'Web' | 'Ios' | 'Android';
export type TestCaseStatus = 'Draft' | 'Active' | 'Archived' | 'Proposed' | 'Rejected';
/** Assistant: proposed by a coding assistant (the TestRome plugin for Claude Code). */
export type TestCaseSource = 'Customer' | 'Tester' | 'Assistant';
export type Priority = 'Critical' | 'High' | 'Medium' | 'Low';
export type EvidenceRequirement = 'None' | 'Screenshot' | 'Video';
export type CheckpointType = 'None' | 'Route' | 'Event';
export type CommunityRole = 'Lead' | 'Tester';
export type MemberStatus = 'Invited' | 'Active' | 'Removed' | 'Requested';
export type DeviceKind = 'Desktop' | 'Phone' | 'Tablet';
export type CycleStatus = 'Draft' | 'Active' | 'Closed';
export type DistributionMode = 'EveryoneRunsAll' | 'NTestersPerCase';
export type AssignmentStatus = 'Assigned' | 'InProgress' | 'Submitted' | 'Accepted' | 'Rejected' | 'Expired';
export type ExecutionKind = 'Scripted' | 'Exploratory';
export type ExecutionStatus = 'InProgress' | 'Submitted' | 'Abandoned';
export type Outcome = 'Passed' | 'Failed' | 'Blocked';
export type ReviewStatus = 'Pending' | 'Accepted' | 'Rejected';
export type StepStatus = 'NotRun' | 'Passed' | 'Failed' | 'Blocked' | 'Skipped';
export type SessionEventType =
  | 'Navigation' | 'Click' | 'Input' | 'ConsoleError' | 'ConsoleWarning'
  | 'NetworkRequest' | 'NetworkError' | 'Checkpoint' | 'VisibilityChange' | 'Heartbeat' | 'Session';
export type Severity = 'Critical' | 'Major' | 'Minor' | 'Trivial';
export type BugStatus = 'New' | 'Confirmed' | 'Duplicate' | 'Rejected' | 'Fixed';
export type ConfidenceBand = 'Trusted' | 'Check' | 'Suspicious';
export type ReasonCode =
  | 'NoRecording' | 'CheckpointMissing' | 'StepTooFast' | 'NoInteraction'
  | 'EnvironmentMismatch' | 'KnownIssuePassed' | 'DuplicateEvidence' | 'CopiedText'
  | 'NoVideo' | 'ShortVideo' | 'DuplicateVideo';
/** Web apps are recorded by the SDK; mobile apps by a screen video the tester uploads. */
export type RecordingMode = 'Sdk' | 'ScreenVideo';

// ----------------------------------------------------------------------------- errors
/** RFC 7807 problem details. `errors` is present for validation failures (field -> messages). */
export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  errors?: Record<string, string[]>;
  /** A machine-readable reason ('nda_required', 'membership_required'), with what it is about. */
  code?: string;
  audience?: 'Organization' | 'Tester';
  organizationId?: string | null;
}

// ----------------------------------------------------------------------------- auth & me
/** intent 'tester' creates a freelancer profile at sign-up. */
export interface RegisterRequest {
  email: string; password: string; displayName: string; intent?: 'tester' | 'customer' | null;
  /** Must be true: the privacy notice was shown and read. */
  acceptPrivacyNotice: boolean;
  /** The optional device-details consent, when answered at sign-up. */
  deviceDetails?: boolean | null;
}
export interface LoginRequest { email: string; password: string; }
export interface RefreshRequest { refreshToken: string; }
export interface AuthResponse {
  accessToken: string;
  accessTokenExpiresAt: IsoDate;
  refreshToken: string;
  user: UserDto;
}
export interface UserDto { id: Guid; email: string; displayName: string; locale: string; }

export interface MeOrganizationDto { id: Guid; name: string; role: OrgRole; }
/** `isPublic`: a freelance team (its leads have the team panel). */
export interface MeCommunityDto { id: Guid; name: string; role: CommunityRole; status: MemberStatus; isPublic?: boolean; }
export interface MeDto extends UserDto {
  organizations: MeOrganizationDto[];
  communities: MeCommunityDto[];
  /** Listed in the API's Platform:AdminEmails: may read every log line and manage payments. */
  isPlatformAdmin: boolean;
  /** Has a freelancer profile (a tester even without a community). */
  hasTesterProfile: boolean;
  /** Required notices not yet acknowledged in their current version (the app asks before anything else). */
  pendingConsents: ConsentKind[];
  /** Answer to the optional device-details consent for its current text; null until answered. */
  deviceDetailsConsent: boolean | null;
  /** A tester who has not saved a CV yet: the app asks for it once, before anything else (every field is optional). */
  needsCv?: boolean;
  /** The person's code on watermarks of confidential jobs ("TR-7KQ2MX"). */
  watermarkCode?: string | null;
  /** Each of the person's organizations, and their own membership when they test on the marketplace. */
  memberships?: MeMembershipDto[];
  /** Commission added on top of what testers earn (0 while memberships pay for the platform). */
  platformFeePercent?: number;
}
export interface UpdateMeRequest { displayName: string; locale: string; }

export interface DeviceDto {
  id: Guid; kind: DeviceKind; os: string; osVersion: string | null; browser: string | null; model: string | null;
  label: string; // server-built, e.g. "Phone · iOS 18 · Safari · iPhone 15"
}
export interface DeviceRequest { kind: DeviceKind; os: string; osVersion?: string | null; browser?: string | null; model?: string | null; }

// ----------------------------------------------------------------------------- organizations
export interface OrganizationDto { id: Guid; name: string; role: OrgRole; createdAt: IsoDate; }
export interface CreateOrganizationRequest { name: string; }
export interface OrgMemberDto { userId: Guid; email: string; displayName: string; role: OrgRole; }
export interface AddOrgMemberRequest { email: string; role: OrgRole; }
export interface PartnerCommunityDto { communityId: Guid; name: string; memberCount: number; linkedAt: IsoDate; }
export interface LinkCommunityRequest { partnerCode: string; }

// ----------------------------------------------------------------------------- projects
export interface ScoringWeights {
  noRecording: number;          // default 40
  checkpointMissing: number;    // default 25 (per step)
  stepTooFast: number;          // default 10 (per step)
  noInteraction: number;        // default 10 (per step)
  environmentMismatch: number;  // default 15
  knownIssuePassed: number;     // default 30
  duplicateEvidence: number;    // default 20
  copiedText: number;           // default 10
  noVideo: number;              // default 40 (screen-video jobs)
  shortVideo: number;           // default 20
  duplicateVideo: number;       // default 40
}
export interface ProjectDto {
  id: Guid; organizationId: Guid; name: string; appUrl: string; platform: Platform;
  publicKey: string; allowedOrigins: string[]; recordingRetentionDays: number;
  scoringWeights: ScoringWeights; createdAt: IsoDate;
}
export interface ProjectSummaryDto {
  id: Guid; name: string; appUrl: string; platform: Platform;
  activeCycles: number; openBugs: number; pendingReviews: number;
}
export interface UpsertProjectRequest {
  name: string; appUrl: string; platform: Platform; allowedOrigins: string[]; recordingRetentionDays?: number;
}
export interface ProjectOverviewDto {
  project: ProjectDto;
  activeCycles: number;
  outcomes: { passed: number; failed: number; blocked: number };
  openBugs: number;
  pendingReviews: number;
  averageConfidence: number | null;
  bands: { trusted: number; check: number; suspicious: number };
  recentCycles: CycleSummaryDto[];
}

// ----------------------------------------------------------------------------- suites & test cases
export interface SuiteDto { id: Guid; projectId: Guid; name: string; description: string | null; caseCount: number; }
export interface UpsertSuiteRequest { name: string; description?: string | null; }

export interface TestStepDto {
  order: number;               // 1-based
  action: string;
  expectedResult: string;
  evidenceRequirement: EvidenceRequirement;
  checkpointType: CheckpointType;
  checkpointValue: string | null; // route pattern (/checkout/success, /orders/:id, /docs/*) or event name
  minExpectedSeconds: number | null;
}
export type TestStepInput = Omit<TestStepDto, 'order'>; // order = array index + 1

export interface TestCaseSummaryDto {
  id: Guid; key: string; title: string; suiteId: Guid | null; priority: Priority; status: TestCaseStatus;
  source: TestCaseSource; stepCount: number; revision: number; updatedAt: IsoDate; createdByName: string;
  /** The coding assistant's batch a proposal came with. */
  submissionId?: Guid | null;
}
export interface TestCaseDto extends TestCaseSummaryDto {
  projectId: Guid; preconditions: string | null; steps: TestStepDto[]; knownIssueIds: Guid[];
  proposedInCycleId: Guid | null; reviewNote: string | null;
}
export interface UpsertTestCaseRequest {
  title: string; preconditions?: string | null; priority: Priority; status?: TestCaseStatus;
  suiteId?: Guid | null; steps: TestStepInput[];
}
export interface ImportCsvRequest { suiteId: Guid; csv: string; dryRun: boolean; }
/** CSV columns (header row required, ; or , separated):
 *  case_key,title,priority,preconditions,step_action,step_expected,evidence,checkpoint_type,checkpoint_value,min_seconds
 *  Rows with the same case_key form one test case; step order = row order. */
export interface ImportCsvResult {
  cases: { key: string; title: string; stepCount: number }[];
  errors: { line: number; message: string }[];
  created: number; // 0 when dryRun
}
export interface ApproveProposalRequest { suiteId: Guid; }
export interface RejectProposalRequest { note: string; }

// ----------------------------------------------------------------------------- known issues
export interface KnownIssueDto { id: Guid; title: string; description: string | null; active: boolean; testCaseIds: Guid[]; createdAt: IsoDate; }
export interface UpsertKnownIssueRequest { title: string; description?: string | null; active: boolean; testCaseIds: Guid[]; }

// ----------------------------------------------------------------------------- communities
export interface CommunitySummaryDto {
  id: Guid; name: string; description: string | null; myRole: CommunityRole; memberCount: number; activeCycles: number;
  /** Listed in the team directory (freelancers can ask to join; the lead can take marketplace jobs). */
  isPublic: boolean;
}
export interface CommunityMemberDto {
  userId: Guid; displayName: string; email: string; role: CommunityRole; status: MemberStatus; joinedAt: IsoDate | null;
  devices: DeviceDto[]; openAssignments: number; reputation: number;
  sharePercent: number | null; requestMessage: string | null; // lead only
}
export interface CommunityDto extends CommunitySummaryDto {
  partnerCode: string | null; // only visible to leads
  members: CommunityMemberDto[];
  partners: { organizationId: Guid; name: string }[];
  pendingRequests: number;
}
export interface UpsertCommunityRequest { name: string; description?: string | null; isPublic?: boolean; }
export interface UpdateMemberRequest { role: CommunityRole; status: MemberStatus; }
export interface CreateInviteRequest { role: CommunityRole; maxUses?: number | null; expiresInDays?: number | null; }
export interface InviteDto { id: Guid; token: string; role: CommunityRole; uses: number; maxUses: number | null; expiresAt: IsoDate | null; createdAt: IsoDate; }
export interface InvitePreviewDto { communityId: Guid; communityName: string; role: CommunityRole; invitedBy: string; valid: boolean; }
export interface LeaderboardEntryDto {
  userId: Guid; displayName: string; points: number; acceptedExecutions: number; confirmedBugs: number; rank: number;
}

// ----------------------------------------------------------------------------- cycles
export interface EnvironmentTarget { label: string; deviceKind?: DeviceKind | null; os?: string | null; browser?: string | null; }
export type CycleSourcing = 'Community' | 'Applications' | 'OpenPool';
export type ListingVisibility = 'Public' | 'InviteOnly';
/** What a tester earns (TRY); the platform fee is added on top for the customer. */
export interface JobPricing {
  currency: string; perAcceptedRun: number | null; bugCritical: number | null; bugMajor: number | null; bugMinor: number | null;
  bugTrivial: number | null; fixedFee: number | null;
}
export interface JobRequirements {
  minReputation: number | null; languages: string[] | null; requireMatchingDevice: boolean; maxParticipants: number | null;
  applyBy: IsoDate | null; claimHours: number; maxOpenClaims: number;
  minLevel?: TesterLevel | null;
}
export interface UpsertCycleRequest {
  name: string; instructions?: string | null; appVersion?: string | null; targetUrl: string;
  communityId: Guid | null; startsAt?: IsoDate | null; dueAt?: IsoDate | null;
  distributionMode: DistributionMode; testersPerCase?: number | null; allowExploratory: boolean;
  environmentMatrix: EnvironmentTarget[]; testCaseIds: Guid[];
  sourcing: CycleSourcing; visibility: ListingVisibility; summary?: string | null; requirements: JobRequirements; pricing: JobPricing;
  /** 1–3; empty means "what the project's platform suggests". */
  categories: JobCategory[];
  /** Budget set aside from the balance when the job is published (0 or null = none). */
  escrowAmount?: number | null;
  recordingMode?: RecordingMode | null;
  /** Confidential job: testers accept the agreement (ndaText, or the default text when empty) first; screens are watermarked. */
  confidential?: boolean;
  ndaText?: string | null;
}
export interface CycleProgress {
  total: number; assigned: number; inProgress: number; submitted: number; accepted: number; rejected: number; expired: number;
}
export interface CycleSummaryDto {
  id: Guid; projectId: Guid; name: string; status: CycleStatus; communityId: Guid | null; communityName: string | null;
  startsAt: IsoDate | null; dueAt: IsoDate | null; progress: CycleProgress; averageConfidence: number | null; openBugs: number;
  sourcing: CycleSourcing; isPaid: boolean; applicants: number; participants: number;
}
export interface CycleDto extends CycleSummaryDto {
  instructions: string | null; appVersion: string | null; targetUrl: string; distributionMode: DistributionMode;
  testersPerCase: number | null; allowExploratory: boolean; environmentMatrix: EnvironmentTarget[];
  testCases: { id: Guid; key: string; title: string; priority: Priority }[];
  activatedAt: IsoDate | null; closedAt: IsoDate | null;
  visibility: ListingVisibility; summary: string | null; requirements: JobRequirements; pricing: JobPricing; platformFeePercent: number;
  categories: JobCategory[];
  /** Planned at publish / currently reserved from the organization's balance ("ödeme güvencede"). */
  escrowPlanned: number; escrowHeld: number;
  recordingMode: RecordingMode;
  confidential?: boolean;
  /** The customer's own agreement text (null: the default text). */
  ndaText?: string | null;
  /** How many testers accepted the agreement. */
  ndaAcceptances?: number;
  /** The CI run that started this cycle (a build from a pipeline). */
  ci?: CycleCiDto | null;
}
export interface ActivateCycleResponse { assignmentsCreated: number; }

export interface BoardCellDto {
  assignmentId: Guid; testCaseId: Guid; testerUserId: Guid; status: AssignmentStatus; targetEnvironment: string | null;
  executionId: Guid | null; outcome: Outcome | null; confidenceScore: number | null; band: ConfidenceBand | null;
  reviewStatus: ReviewStatus | null; receiving: boolean; // receiving = SDK events in the last 30s
}
export interface BoardDto {
  cycle: CycleDto;
  testers: { userId: Guid; displayName: string }[];
  testCases: { id: Guid; key: string; title: string; priority: Priority }[];
  cells: BoardCellDto[];
  exploratory: ExecutionSummaryDto[];
}

// ----------------------------------------------------------------------------- tester: assignments & executions
export interface EnvironmentInfo {
  deviceKind?: DeviceKind | null; os?: string | null; osVersion?: string | null; browser?: string | null; model?: string | null; label?: string | null;
}
export interface MyAssignmentGroupDto {
  cycle: {
    id: Guid; name: string; projectName: string; dueAt: IsoDate | null; instructions: string | null; targetUrl: string; allowExploratory: boolean; status: CycleStatus;
    recordingMode: RecordingMode;
    /** Android + SDK: the app is opened from the phone (QR or pairing code), not in a browser window. */
    platform?: Platform;
    /** A confidential job; until the tester accepts its agreement the instructions, address and steps stay hidden. */
    ndaRequired?: boolean;
    ndaAccepted?: boolean;
  };
  assignments: MyAssignmentDto[];
}
/** Cycle as seen by a tester (also returned by GET /api/my/cycles for starting exploratory sessions). */
export type MyCycleDto = MyAssignmentGroupDto['cycle'];
export interface MyAssignmentDto {
  id: Guid; status: AssignmentStatus; dueAt: IsoDate | null; targetEnvironment: string | null;
  testCase: { id: Guid; key: string; title: string; priority: Priority; stepCount: number };
  latestExecutionId: Guid | null; latestOutcome: Outcome | null; reviewStatus: ReviewStatus | null; reviewNote: string | null;
  /** Team work: the team, and the lead's note for this task. */
  teamName?: string | null; teamNote?: string | null;
}
export interface AssignmentDetailDto {
  id: Guid; status: AssignmentStatus; dueAt: IsoDate | null; targetEnvironment: string | null;
  cycle: MyAssignmentGroupDto['cycle'];
  testCase: TestCaseDto;
  executions: ExecutionSummaryDto[];
  teamName?: string | null; teamNote?: string | null;
}
export interface StartExecutionRequest { deviceId?: Guid | null; declaredEnvironment?: EnvironmentInfo | null; }
export interface StartExploratoryRequest extends StartExecutionRequest { charter: string; timeboxMinutes: number; }
export interface StartExecutionResponse { executionId: Guid; launchUrl: string; tokenExpiresAt: IsoDate; }

export interface AttachmentDto {
  id: Guid; fileName: string; contentType: string; sizeBytes: number; stepOrder: number | null; url: string; createdAt: IsoDate;
}
export interface StepResultDto extends TestStepDto {
  status: StepStatus; actualResult: string | null; startedAt: IsoDate | null; completedAt: IsoDate | null;
  durationMs: number | null; checkpointMatched: boolean | null; attachments: AttachmentDto[];
  /** Screen-video runs: where the step starts in the recording. */
  videoOffsetMs: number | null;
}
export interface ExecutionSummaryDto {
  id: Guid; kind: ExecutionKind; status: ExecutionStatus; testCaseId: Guid | null; testCaseKey: string | null; title: string;
  testerUserId: Guid; testerName: string; startedAt: IsoDate; submittedAt: IsoDate | null; outcome: Outcome | null;
  confidenceScore: number | null; band: ConfidenceBand | null; reviewStatus: ReviewStatus;
}
export interface ExecutionDto extends ExecutionSummaryDto {
  cycleId: Guid; assignmentId: Guid | null; charter: string | null; timeboxMinutes: number | null; notes: string | null;
  declaredEnvironment: EnvironmentInfo | null; detectedEnvironment: DetectedEnvironment | null;
  steps: StepResultDto[]; attachments: AttachmentDto[]; // attachments not tied to a step
  lastEventAt: IsoDate | null; tokenExpiresAt: IsoDate | null; reviewNote: string | null;
  recordingMode: RecordingMode; screenVideo: ScreenVideoDto | null; videoStartedAt: IsoDate | null;
  /** Detailed device facts, only with the tester's consent. */
  deviceDetails: DeviceDetails | null;
  /** The app's platform: Android + SDK means the TestRome Android SDK records the run inside the app. */
  platform?: Platform;
  /** A confidential job: the runner is watermarked with the viewer's name and code. */
  confidential?: boolean;
}

// ----------------------------------------------------------------------------- the phone (QR link) and app pairing
/** The run's phone link; the page is `/m/{code}`. */
export interface PhoneLinkDto { code: string; expiresAt: IsoDate; }
/** Typed into the app (Android SDK) to connect it to the run; single-use. Shown as "K7P-4QX". */
export interface PairingCodeDto { code: string; expiresAt: IsoDate; }
export interface PhoneVideoDto { fileName: string; sizeBytes: number; durationMs: number | null; uploadedAt: IsoDate; }
/** What the phone page shows (no sign-in). */
export interface PhoneSessionDto {
  label: string; testerName: string; cycleName: string; recordingMode: RecordingMode; platform: Platform;
  canUploadVideo: boolean; canOpenApp: boolean; screenVideo: PhoneVideoDto | null; expiresAt: IsoDate;
}
export interface PhoneLaunchDto { launchUrl: string; }
export interface ScreenVideoDto {
  attachmentId: Guid; fileName: string; contentType: string; sizeBytes: number; durationMs: number | null; url: string; uploadedAt: IsoDate;
  /** What the file says about the phone (size; make, model and OS version when the phone wrote them). */
  info: ScreenVideoInfo | null;
}
/** What the browser lets a page know about the device (collected only with the tester's consent). Every field may be missing. */
export interface DeviceDetails {
  osName?: string | null; osVersion?: string | null; architecture?: string | null; bitness?: number | null; model?: string | null; formFactor?: string | null;
  browser?: string | null; browserVersion?: string | null;
  screenWidth?: number | null; screenHeight?: number | null; availableWidth?: number | null; availableHeight?: number | null; colorDepth?: number | null;
  pixelRatio?: number | null; orientation?: string | null;
  cpuCores?: number | null; memoryGb?: number | null; gpuVendor?: string | null; gpuRenderer?: string | null;
  connectionType?: string | null; downlinkMbps?: number | null; rttMs?: number | null; saveData?: boolean | null; storageQuotaMb?: number | null;
  touchPoints?: number | null; colorScheme?: string | null; reducedMotion?: boolean | null; languages?: string[] | null;
  collectedAt: IsoDate;
}
export interface ScreenVideoInfo {
  width?: number | null; height?: number | null; make?: string | null; model?: string | null; osName?: string | null; osVersion?: string | null; recordedAt?: IsoDate | null;
}
export interface VideoMarkerDto { stepOrder: number; offsetMs: number | null; }
export interface UpdateStepRequest { status: StepStatus; actualResult?: string | null; }
export interface SubmitExecutionRequest { notes?: string | null; }
export interface RecordingStatusDto { receiving: boolean; lastEventAt: IsoDate | null; eventCount: number; chunkCount: number; }
export interface DetectedEnvironment {
  userAgent: string; browser: string | null; browserVersion: string | null; os: string | null; deviceKind: DeviceKind | null;
  viewport: string | null; language: string | null; timezone: string | null;
}

// ----------------------------------------------------------------------------- review
export interface ConfidenceReasonDto {
  code: ReasonCode; penalty: number; detail: string; stepOrder: number | null; at: IsoDate | null;
}
export interface ReviewQueueItemDto {
  executionId: Guid; cycleId: Guid; cycleName: string; kind: ExecutionKind; testCaseKey: string | null; title: string;
  testerName: string; submittedAt: IsoDate; outcome: Outcome | null; confidenceScore: number | null; band: ConfidenceBand | null;
  reasonCount: number;
}
export interface SessionEventDto {
  id: Guid; at: IsoDate; type: SessionEventType; summary: string; data: Record<string, unknown>; stepOrder: number | null;
}
export interface ReviewDataDto {
  execution: ExecutionDto;
  testCase: TestCaseDto | null;
  score: number | null;
  band: ConfidenceBand | null;
  reasons: ConfidenceReasonDto[];
  events: SessionEventDto[];
  recording: { startedAt: IsoDate | null; chunks: { sequence: number; firstEventAt: IsoDate; lastEventAt: IsoDate; eventCount: number; url: string }[] };
  bugs: BugReportSummaryDto[];
  previousExecutionId: Guid | null; // neighbours in the review queue (for J/K navigation)
  nextExecutionId: Guid | null;
  /** The run's time and its pauses of at least a minute. */
  timing: RunTimingDto | null;
}
/** A pause: nothing was done from `from` to `to`; `hiddenMs` of it the app was off screen; `stepOrder` the step going on (null between steps). */
export interface IdleGapDto { from: IsoDate; to: IsoDate; durationMs: number; hiddenMs: number; fromKind: string; toKind: string; stepOrder: number | null; }
export interface RunTimingDto { totalMs: number; actions: number; gaps: IdleGapDto[]; }
export interface ReviewDecisionRequest { decision: 'Accepted' | 'Rejected'; note?: string | null; }

// ----------------------------------------------------------------------------- bugs
export interface CreateBugRequest {
  executionId?: Guid | null; title: string; severity: Severity; stepsToReproduce: string; expectedResult: string;
  actualResult: string; stepOrder?: number | null; recordingOffsetMs?: number | null; environment?: EnvironmentInfo | null;
}
export interface BugReportSummaryDto {
  id: Guid; number: number; title: string; severity: Severity; status: BugStatus; reporterName: string; cycleId: Guid;
  cycleName: string; executionId: Guid | null; createdAt: IsoDate; duplicateOfId: Guid | null;
  /** Reports marked as duplicates of this one (it is their group's main record: the first report). */
  duplicateCount: number;
  /** The main record's number when this one is a duplicate. */
  duplicateOfNumber: number | null;
  /** An earlier report in the cycle this one looks like (until the customer decides). */
  suspectedDuplicateOfId: Guid | null;
  suspectedDuplicateNumber: number | null;
  /** Fixes a developer's coding assistant reported for it. */
  fixCount?: number;
}
export interface BugReportDto extends BugReportSummaryDto {
  projectId: Guid; reporterUserId: Guid; stepsToReproduce: string; expectedResult: string; actualResult: string;
  stepOrder: number | null; recordingOffsetMs: number | null; environment: EnvironmentInfo | null;
  attachments: AttachmentDto[]; triagedByName: string | null; triagedAt: IsoDate | null; triageNote: string | null;
  duplicates: BugReportSummaryDto[];
  /** The bug's issues in the organization's trackers (empty for the tester who reported it). */
  links: BugLinkDto[];
  /** The device of the run it came from ("Windows 11 (64 bit) · Chrome 141… · 8 çekirdek …" or the screen recording's phone). */
  deviceSummary: string | null;
  confidential?: boolean;
  /** How alike the suspected duplicate is (0–1). */
  duplicateScore: number | null;
  /** Fixes reported by developers' coding assistants (the customer's side only), newest first. */
  fixes?: BugFixDto[] | null;
  /** The case the bug was found in (null for exploratory finds). */
  caseKey?: string | null;
}
export interface TriageBugRequest { status: BugStatus; duplicateOfId?: Guid | null; note?: string | null; }
export type DuplicateReason = 'sameStep' | 'sameCase' | 'similarTitle' | 'similarResult';
export interface SimilarBugsRequest { title: string; actualResult: string; executionId?: Guid | null; stepOrder?: number | null; }
/** A possible duplicate; testers get no reporter name. */
export interface SimilarBugDto {
  id: Guid; number: number; title: string; severity: Severity; status: BugStatus; score: number; reasons: DuplicateReason[]; mine: boolean;
  createdAt: IsoDate; cycleId: Guid; cycleName: string; reporterName: string | null;
}

// ----------------------------------------------------------------------------- reputation
export interface ReputationEventDto { id: Guid; points: number; reason: string; communityName: string | null; createdAt: IsoDate; }
export interface MyReputationDto { total: number; events: ReputationEventDto[]; }

// ----------------------------------------------------------------------------- realtime (SignalR hub at /hubs/live)
/** Client invokes `JoinCycle(cycleId)` / `LeaveCycle(cycleId)`. Server pushes `boardChanged`. */
export interface BoardChangedEvent {
  cycleId: Guid; kind: 'assignment' | 'execution' | 'score' | 'review' | 'bug' | 'recording';
  assignmentId: Guid | null; executionId: Guid | null;
}

// ----------------------------------------------------------------------------- logs (platform activity + in-app events)
export type LogSource = 'platform' | 'app';
export type ActivityCategory =
  | 'auth' | 'account' | 'organization' | 'project' | 'design' | 'cycle' | 'execution' | 'review' | 'bug' | 'community' | 'marketplace'
  | 'billing' | 'system' | 'other';
/**
 * One log line. `source: 'platform'` = something done in TestRome (`kind` is an action key such as `execution.submitted`);
 * `source: 'app'` = an event the recorder SDK captured in the app under test (`kind` is a SessionEventType, `category` is 'app').
 */
export interface LogEntryDto {
  id: Guid; source: LogSource; at: IsoDate; userId: Guid | null; userName: string | null; kind: string;
  category: ActivityCategory | 'app'; success: boolean; summary: string | null; subject: string | null;
  entityType: string | null; entityId: Guid | null;
  projectId: Guid | null; projectName: string | null; cycleId: Guid | null; cycleName: string | null;
  executionId: Guid | null; executionLabel: string | null; communityName: string | null; stepOrder: number | null;
  data: Record<string, unknown>; statusCode: number | null; error: string | null; ipAddress: string | null; userAgent: string | null;
  durationMs: number | null; method: string | null; path: string | null;
  /** Names of related objects when the action happened (project, cycle, community, organization, execution, user, …). */
  context: Record<string, string>;
  /** For someone's action: ms since their previous action in this view (same scope and project / cycle / run filters). */
  gapMs: number | null;
}
export interface LogPageDto { items: LogEntryDto[]; nextCursor: string | null; }
export interface LogUserDto {
  userId: Guid; name: string; email: string; role: string | null; platformCount: number; appCount: number; sessionCount: number;
  errorCount: number; lastAt: IsoDate | null;
}
export interface LogOptionDto { id: Guid; name: string; parentId: Guid | null; }
export interface LogFiltersDto { users: LogUserDto[]; projects: LogOptionDto[]; cycles: LogOptionDto[]; }

// ----------------------------------------------------------------------------- marketplace: freelancers and teams
export type ParticipantKind = 'Solo' | 'Team';
export type ParticipantStatus = 'Applied' | 'Invited' | 'Accepted' | 'Rejected' | 'Withdrawn' | 'Removed';
export type IneligibleReason = 'Reputation' | 'Language' | 'Device' | 'Closed' | 'Full' | 'Deadline' | 'NotInvited' | 'Level';
/** Marketplace standing: Yeni, Bronz, Gümüş, Altın, Platin. */
export type TesterLevel = 'New' | 'Bronze' | 'Silver' | 'Gold' | 'Platinum';
export const TESTER_LEVELS: readonly TesterLevel[] = ['New', 'Bronze', 'Silver', 'Gold', 'Platinum'];
/** What kind of product a job tests; also the fields a freelancer works in. */
export type JobCategory = 'MobileApp' | 'WebApp' | 'DesktopApp' | 'Game' | 'Api' | 'SmartDevice' | 'Other';
export const JOB_CATEGORIES: readonly JobCategory[] = ['MobileApp', 'WebApp', 'DesktopApp', 'Game', 'Api', 'SmartDevice', 'Other'];
/** A job can be listed under at most this many categories. */
export const MAX_JOB_CATEGORIES = 3;
export interface TesterStatsDto {
  reputation: number; acceptedRuns: number; rejectedRuns: number; confirmedBugs: number; averageConfidence: number | null; cycles: number;
  level: TesterLevel; qualified: boolean; identityVerified: boolean;
}
export type LevelCheckCode = 'acceptedRuns' | 'confirmedBugs' | 'acceptanceRate' | 'averageConfidence' | 'qualified' | 'identityVerified' | 'rating';
export interface LevelProgressDto {
  level: TesterLevel; next: TesterLevel | null; nextChecks: { code: LevelCheckCode; met: boolean; have: number; need: number }[];
  qualified: boolean; qualifiedAt: IsoDate | null; identityVerified: boolean; identityVerifiedAt: IsoDate | null; academyJobId: Guid | null;
  stats: TesterStatsDto;
}
export interface TesterProfileDto {
  userId: Guid; displayName: string; headline: string | null; bio: string | null; skills: string[]; languages: string[];
  city: string | null; country: string | null; isListed: boolean; isAvailable: boolean; createdAt: IsoDate; categories: JobCategory[];
}
export interface UpsertTesterProfileRequest {
  headline?: string | null; bio?: string | null; skills: string[]; languages: string[]; city?: string | null; country?: string | null;
  isListed: boolean; isAvailable: boolean; categories: JobCategory[];
}
export interface TesterPublicDto {
  profile: TesterProfileDto; stats: TesterStatsDto; devices: string[]; teams: { id: Guid; name: string; role: CommunityRole }[]; memberSince: IsoDate;
  rating: RatingSummaryDto;
  /** The viewer may read this tester's CV (customers, the leads of their teams, the tester, platform admins). */
  canViewCv?: boolean;
  /** The tester has saved a CV (told only to those who may read it). */
  hasCv?: boolean;
}

// ----------------------------------------------------------------------------- CV
export type LanguageLevel = 'Basic' | 'Intermediate' | 'Advanced' | 'Native';
export const LANGUAGE_LEVELS: readonly LanguageLevel[] = ['Basic', 'Intermediate', 'Advanced', 'Native'];
export type TestingType =
  | 'Manual' | 'Exploratory' | 'Regression' | 'Automation' | 'Api' | 'Performance' | 'Security' | 'Accessibility' | 'Usability' | 'Localization' | 'Mobile' | 'Game';
export const TESTING_TYPES: readonly TestingType[] = [
  'Manual', 'Exploratory', 'Regression', 'Automation', 'Api', 'Performance', 'Security', 'Accessibility', 'Usability', 'Localization', 'Mobile', 'Game',
];
export interface CvExperience { title: string | null; company: string | null; startYear: number | null; endYear: number | null; current: boolean; description: string | null; }
export interface CvEducation { school: string | null; degree: string | null; field: string | null; startYear: number | null; endYear: number | null; }
export interface CvCertificate { name: string | null; issuer: string | null; year: number | null; }
export interface CvLanguage { name: string | null; level: LanguageLevel | null; }
export interface CvLink { label: string | null; url: string | null; }
/** Everything is optional. */
export interface CvContent {
  summary: string | null; testingYears: number | null; experience: CvExperience[]; education: CvEducation[]; certificates: CvCertificate[];
  languages: CvLanguage[]; tools: string[]; testingTypes: TestingType[]; links: CvLink[];
}
/** The signed-in tester's CV; `submitted` is false until it was saved once. */
export interface CvDto { content: CvContent; submitted: boolean; updatedAt: IsoDate | null; }
/** "Fill from PDF": with `ai` the PDF itself is sent (Claude reads it); otherwise only the text the browser read from it. */
export interface CvImportOptionsDto { ai: boolean; maxBytes: number; }
export type CvImportMethod = 'Rules' | 'Ai';
/** A draft of the CV form (nothing is saved), the parts found in it, and notes for the tester. */
export interface CvImportResultDto { content: CvContent; method: CvImportMethod; found: string[]; warnings: string[]; }
/** A tester's CV as a customer reads it; `content` is null when they have not filled it in. */
export interface TesterCvDto { userId: Guid; displayName: string; headline: string | null; content: CvContent | null; updatedAt: IsoDate | null; }
export interface TesterCardDto {
  userId: Guid; displayName: string; headline: string | null; skills: string[]; languages: string[]; city: string | null;
  isAvailable: boolean; stats: TesterStatsDto; devices: string[]; categories: JobCategory[]; rating: RatingSummaryDto;
}
export interface TeamCardDto {
  id: Guid; name: string; description: string | null; memberCount: number; leadName: string | null; leadUserId: Guid | null; skills: string[];
  stats: TesterStatsDto; myStatus: MemberStatus | null; myRole: CommunityRole | null; categories: JobCategory[]; rating: RatingSummaryDto;
  /** Featured ("öne çıkan") in the team directory until then; null when not featured now. */
  featuredUntil: IsoDate | null;
}
export interface TeamMemberPublicDto { userId: Guid; displayName: string; role: CommunityRole; headline: string | null; stats: TesterStatsDto; devices: string[]; }
export interface TeamPublicDto { team: TeamCardDto; members: TeamMemberPublicDto[]; }
export interface MemberShareDto { userId: Guid; sharePercent: number | null; }
export interface TeamWorkItemDto {
  assignmentId: Guid; cycleId: Guid; cycleName: string; projectName: string; organizationName: string; testCaseKey: string; testCaseTitle: string;
  targetEnvironment: string | null; status: AssignmentStatus; holderUserId: Guid; holderName: string; dueAt: IsoDate | null; started: boolean;
  /** The lead's note for whoever runs it. */
  teamNote?: string | null;
  /** Active members with a device for the task's environment (empty when the job has none). */
  fitMemberIds?: Guid[] | null;
}
export interface TeamJobDto {
  participantId: Guid; cycleId: Guid; cycleName: string; projectName: string; organizationName: string; status: ParticipantStatus; cycleStatus: CycleStatus;
}
export interface TeamWorkDto { items: TeamWorkItemDto[]; jobs: TeamJobDto[]; shares: MemberShareDto[]; }

// ----------------------------------------------------------------------------- team offers and panel
export type TeamOfferStatus = 'Pending' | 'Accepted' | 'Declined' | 'Withdrawn';
export interface TeamOfferDto {
  id: Guid; teamId: Guid; userId: Guid; userName: string; headline: string | null; devices: string[]; invitedByName: string; message: string | null;
  status: TeamOfferStatus; createdAt: IsoDate; answeredAt: IsoDate | null;
}
export interface MyTeamOfferDto { id: Guid; team: TeamCardDto; invitedByName: string; message: string | null; createdAt: IsoDate; }
export interface TeamPanelStatsDto {
  activeJobs: number; pendingApplications: number; openTasks: number; withLeads: number; inProgress: number; dueSoon: number; overdue: number;
  submittedLast30: number; acceptedLast30: number; rejectedLast30: number; earnedLast30: number; pendingEarnings: number; paidTotal: number; currency: string;
}
export interface TeamPanelMemberDto {
  userId: Guid; displayName: string; role: CommunityRole; sharePercent: number | null; devices: string[]; openTasks: number; inProgress: number;
  submittedLast30: number; acceptedLast30: number; rejectedLast30: number; earnedLast30: number; pendingEarnings: number;
  lastActiveAt: IsoDate | null; joinedAt: IsoDate | null;
}
export interface TeamPanelDto {
  teamId: Guid; name: string; isPublic: boolean; rating: RatingSummaryDto; stats: TeamPanelStatsDto; members: TeamPanelMemberDto[];
  pendingRequests: number; pendingOffers: number;
}
export interface TeamSkipDto { assignmentId: Guid; testCaseKey: string; reason: string; }
export interface TeamHandoutDto { assignmentId: Guid; testCaseKey: string; cycleName: string; userId: Guid; userName: string; deviceFit: boolean; }
export interface TeamDistributionDto { handouts: TeamHandoutDto[]; skipped: TeamSkipDto[]; applied: boolean; work: TeamWorkDto | null; }

// ----------------------------------------------------------------------------- marketplace: jobs
export interface JobCardDto {
  cycleId: Guid; title: string; organizationName: string; projectName: string; platform: Platform; sourcing: CycleSourcing; summary: string | null;
  pricing: JobPricing; caseCount: number; slotsLeft: number | null; participants: number; dueAt: IsoDate | null; applyBy: IsoDate | null;
  environments: EnvironmentTarget[]; requirements: JobRequirements; eligible: boolean; ineligibleReasons: IneligibleReason[];
  myStatus: ParticipantStatus | null; myParticipantId: Guid | null; myTeamName: string | null; myOpenClaims: number;
  publishedAt: IsoDate | null; visibility: ListingVisibility; categories: JobCategory[]; organizationId: Guid; customerRating: RatingSummaryDto;
  /** What the customer has set aside for this job (0 = no prepaid budget). */
  escrowHeld: number;
  recordingMode: RecordingMode;
  /** The TestRome academy job (a reviewed practice run qualifies the tester). */
  isQualification: boolean;
  /** Featured ("öne çıkan") on the job board until then; null when not featured now. */
  featuredUntil: IsoDate | null;
  /** A confidential job (an agreement to accept first). */
  confidential?: boolean;
}
export interface JobDetailDto {
  job: JobCardDto; appVersion: string | null; allowExploratory: boolean;
  cases: { key: string; title: string; priority: Priority; stepCount: number; freeSlots: number | null }[];
  myTeams: { id: Guid; name: string; memberCount: number; eligible: boolean; ineligibleReasons: IneligibleReason[]; status: ParticipantStatus | null }[];
  myStats: TesterStatsDto; hasProfile: boolean; platformFeePercent: number;
  /** False for a confidential job whose agreement the caller has not accepted (cases and version are hidden). */
  ndaAccepted?: boolean;
}
export interface ApplyRequest { teamId?: Guid | null; message?: string | null; }
export interface ClaimResponse { assignmentId: Guid; testCaseKey: string; targetEnvironment: string | null; dueAt: IsoDate | null; }
export interface MyParticipationDto {
  participantId: Guid; cycleId: Guid; cycleName: string; projectName: string; organizationName: string; sourcing: CycleSourcing;
  kind: ParticipantKind; teamId: Guid | null; teamName: string | null; iAmLead: boolean; status: ParticipantStatus; message: string | null;
  decisionNote: string | null; createdAt: IsoDate; decidedAt: IsoDate | null; cycleStatus: CycleStatus; pricing: JobPricing;
}
export interface ParticipantDto {
  id: Guid; kind: ParticipantKind; status: ParticipantStatus; userId: Guid; userName: string; teamId: Guid | null; teamName: string | null;
  memberCount: number; message: string | null; decisionNote: string | null; createdAt: IsoDate; decidedAt: IsoDate | null; stats: TesterStatsDto;
  devices: string[]; skills: string[]; eligible: boolean; ineligibleReasons: IneligibleReason[]; assignments: number; acceptedRuns: number;
  invited: boolean; rating: RatingSummaryDto;
}
export interface InviteParticipantRequest { userId?: Guid | null; teamId?: Guid | null; message?: string | null; }
/** Why a tester fits a job (`positive`) or less so; `code` is worded by the client (recommend.reason.*), `value` is shown in it. */
export interface MatchReasonDto { code: string; value: string | null; positive: boolean; }
export interface RecommendationDto {
  userId: Guid; displayName: string; headline: string | null; score: number; reasons: MatchReasonDto[]; devices: string[];
  rating: RatingSummaryDto; stats: TesterStatsDto; isAvailable: boolean;
}
export interface RecommendationsDto { testers: RecommendationDto[]; considered: number; ineligible: number; }

// ----------------------------------------------------------------------------- earnings, payouts, billing
export type EarningKind = 'AcceptedRun' | 'ConfirmedBug' | 'FixedFee' | 'Adjustment';
export type EarningStatus = 'Pending' | 'Invoiced' | 'Paid' | 'PaidOut' | 'Void';
export type StatementStatus = 'Open' | 'Paid' | 'Cancelled';
export type PaymentMethod = 'BankTransfer' | 'Card' | 'Balance';
export type PayoutMethod = 'None' | 'BankTransfer' | 'Iyzico';
export type PayoutAccountStatus = 'NotSet' | 'Ready' | 'Failed';
export interface EarningDto {
  id: Guid; kind: EarningKind; status: EarningStatus; amount: number; currency: string; description: string; cycleId: Guid; cycleName: string;
  projectName: string; organizationName: string; teamName: string | null; statementCode: string | null; createdAt: IsoDate;
  /** Who earns it (shown to the customer on a statement). */
  payeeName: string;
}
export interface PayoutAccountDto {
  method: PayoutMethod; status: PayoutAccountStatus; holderName: string; ibanLast4: string | null; email: string | null; phone: string | null;
  address: string | null; lastError: string | null; cardPayoutsAvailable: boolean; updatedAt: IsoDate | null;
}
export interface UpdatePayoutAccountRequest {
  method: PayoutMethod; holderName: string; iban?: string | null; email?: string | null; phone?: string | null; address?: string | null;
  identityNumber?: string | null;
}
export interface PayoutDto { id: Guid; amount: number; currency: string; method: PayoutMethod; reference: string | null; createdAt: IsoDate; }
export interface MyEarningsDto {
  totals: { pending: number; invoiced: number; paid: number; paidOut: number; currency: string };
  entries: EarningDto[]; payouts: PayoutDto[]; account: PayoutAccountDto;
}
export interface BillingInfoDto {
  companyName: string; taxOffice: string | null; taxNumber: string | null; contactName: string; contactSurname: string; email: string;
  phone: string; address: string; city: string; country: string; identityNumber: string | null;
}
export interface UnbilledCycleDto { cycleId: Guid; cycleName: string; projectName: string; amount: number; fee: number; entries: number; status: CycleStatus; }
export interface StatementSummaryDto {
  id: Guid; code: string; cycleId: Guid; cycleName: string; projectName: string; organizationName: string; status: StatementStatus;
  subtotal: number; fee: number; total: number; currency: string; paidWith: PaymentMethod | null; paidAt: IsoDate | null; createdAt: IsoDate;
  organizationId: Guid;
}
export interface StatementLineDto {
  id: Guid; payeeUserId: Guid; payeeName: string; amount: number; fee: number; total: number; cardReady: boolean; releasedAt: IsoDate | null;
}
export type CardBlocker = 'notConfigured' | 'billingMissing' | 'payeesNotReady' | 'notOpen';
export interface StatementDto {
  summary: StatementSummaryDto; lines: StatementLineDto[]; entries: EarningDto[]; cardAvailable: boolean; cardBlockers: CardBlocker[];
  bankTransfer: { accountName: string; bankName: string; iban: string; reference: string }; paymentReference: string | null;
  lastPaymentError: string | null; platformFeePercent: number;
  billing: BillingInfoDto | null; issuer: IssuerDto;
  /** Paying from the prepaid balance (open statements only): the job's reserve is used first. */
  balance: { available: number; heldForJob: number; fromReserve: number; fromAvailable: number; enough: boolean } | null;
}
export interface IssuerDto { name: string; taxOffice: string | null; taxNumber: string | null; address: string | null; email: string | null; website: string | null; }
export interface BillingOverviewDto {
  unbilled: UnbilledCycleDto[]; statements: StatementSummaryDto[]; billing: BillingInfoDto | null; cardPaymentsEnabled: boolean;
}
export interface PayoutDueDto {
  /** Suspended accounts are not paid out until reinstated. */
  suspended: boolean;
  userId: Guid; displayName: string; email: string; amount: number; currency: string; entries: number; method: PayoutMethod;
  accountStatus: PayoutAccountStatus; holderName: string | null; iban: string | null;
}
export interface AdminBillingDto { awaitingTransfer: StatementSummaryDto[]; payoutsDue: PayoutDueDto[]; recentlyPaid: StatementSummaryDto[]; }

// ----------------------------------------------------------------------------- prepaid balance and job budgets
export type WalletEntryKind = 'Deposit' | 'Reserve' | 'Release' | 'Spend' | 'RefundRequested' | 'RefundReturned' | 'Promotion';
export type DepositStatus = 'Pending' | 'Completed' | 'Failed' | 'Cancelled';
export type RefundStatus = 'Requested' | 'Sent' | 'Rejected';
export type ReleaseBlocker = 'active' | 'disputes' | 'unreviewed' | 'untriaged';
export interface ReservationDto {
  cycleId: Guid; cycleName: string; projectName: string; status: CycleStatus; held: number; outstanding: number; releasable: number;
  closedAt: IsoDate | null; autoReleaseAt: IsoDate | null; releaseBlockers: ReleaseBlocker[];
}
export interface DepositDto {
  id: Guid; code: string; organizationId: Guid; organizationName: string; amount: number; currency: string; method: PaymentMethod; status: DepositStatus;
  reference: string | null; lastError: string | null; createdAt: IsoDate; completedAt: IsoDate | null; createdByName: string;
}
export interface WalletRefundDto {
  id: Guid; organizationId: Guid; organizationName: string; amount: number; currency: string; status: RefundStatus; holderName: string; ibanLast4: string;
  /** Platform admins only. */
  iban: string | null; reference: string | null; note: string | null; createdAt: IsoDate; decidedAt: IsoDate | null; requestedByName: string;
}
export interface WalletEntryDto {
  id: Guid; kind: WalletEntryKind; availableDelta: number; reservedDelta: number; availableAfter: number; reservedAfter: number;
  cycleId: Guid | null; cycleName: string | null; statementId: Guid | null; statementCode: string | null; depositCode: string | null;
  createdAt: IsoDate; actorName: string | null; promotionCode: string | null;
}
export interface WalletDto {
  available: number; reserved: number; currency: string; cardPaymentsEnabled: boolean;
  bankTransfer: { accountName: string; bankName: string; iban: string; reference: string };
  reservations: ReservationDto[]; deposits: DepositDto[]; refunds: WalletRefundDto[]; entries: WalletEntryDto[];
  minDeposit: number; maxDeposit: number; organizationName: string;
}
export interface DepositStartedDto { deposit: DepositDto; paymentUrl: string | null; }
export interface AdminWalletDto {
  pendingDeposits: DepositDto[]; pendingRefunds: WalletRefundDto[]; recentDeposits: DepositDto[]; recentRefunds: WalletRefundDto[];
  totalAvailable: number; totalReserved: number;
}

// ----------------------------------------------------------------------------- confidential jobs (NDA)
export interface NdaDto {
  cycleId: Guid; cycleName: string; organizationName: string; required: boolean; text: string; textHash: string;
  acceptedAt: IsoDate | null;
  /** A tester who may accept it now (not the customer's own people, not after the job closed). */
  canAccept: boolean;
}
export interface NdaAcceptanceDto { userId: Guid; displayName: string; acceptedAt: IsoDate; textHash: string; }
export interface NdaAcceptancesDto { textHash: string; items: NdaAcceptanceDto[]; }

// ----------------------------------------------------------------------------- test accounts (the job's vault)
export interface TestAccountDto {
  id: Guid; label: string; username: string;
  /** Handed to one tester (each gets their own); otherwise shared by everyone on the job. */
  exclusive: boolean;
  hasNotes: boolean;
  /** Customer view: who has the exclusive account, and how often its password was seen. */
  assignedUserId: Guid | null; assignedToName: string | null; assignedAt: IsoDate | null; reveals: number; lastRevealAt: IsoDate | null;
  /** Tester view: the exclusive account handed to me. */
  mine: boolean;
}
export interface TestAccountsDto {
  manage: boolean; tester: boolean; items: TestAccountDto[];
  /** Exclusive accounts not handed out yet (a tester without one may take one). */
  exclusiveFree: number;
  /** A confidential job whose agreement the tester has not accepted: nothing is shown. */
  needsNda: boolean;
}
export interface UpsertTestAccountRequest { label: string; username: string; password: string | null; notes: string | null; exclusive: boolean; }
export interface TestAccountSecretDto { id: Guid; label: string; username: string; password: string; notes: string | null; }
export interface TestAccountRevealDto { accountId: Guid; accountLabel: string; userId: Guid; userName: string; at: IsoDate; }

// ----------------------------------------------------------------------------- featured listings ("öne çıkarma")
export type PromotionTarget = 'Job' | 'Team';
export type PromotionPayment = 'Free' | 'Balance' | 'Card';
export type PromotionStatus = 'Pending' | 'Active' | 'Failed' | 'Cancelled';
export type PromotionBlocker = 'notMarketplace' | 'notActive' | 'notPublic';
export interface PromotionPlan { target: PromotionTarget; days: number; price: number; isActive: boolean; }
export interface PromotionDto {
  id: Guid; code: string; target: PromotionTarget; targetId: Guid; targetName: string; days: number; price: number; currency: string;
  payment: PromotionPayment; status: PromotionStatus; startsAt: IsoDate | null; endsAt: IsoDate | null; createdAt: IsoDate; purchasedByName: string;
  lastError: string | null; organizationName: string | null;
}
export interface PromotionOptionsDto {
  target: PromotionTarget; targetId: Guid; targetName: string; blocker: PromotionBlocker | null;
  /** End of the featuring running now (or queued after it). */
  featuredUntil: IsoDate | null;
  freeLeft: number; freePerMonth: number; freeDays: number;
  /** Extra free featurings a month thanks to the team lead's level. */
  levelBonus: number;
  plans: PromotionPlan[]; balanceAllowed: boolean; balance: number | null; cardEnabled: boolean;
  /** Card payments for a job need the organization's billing details (with T.C. kimlik no). */
  billingReady: boolean;
  history: PromotionDto[];
}
export interface PromotionBuyerRequest { name: string; surname: string; identityNumber: string; phone: string; address: string; city: string; }
export interface PurchasePromotionRequest {
  target: PromotionTarget; targetId: Guid; payment: PromotionPayment; days: number; buyer: PromotionBuyerRequest | null;
}
export interface PromotionStartedDto { promotion: PromotionDto; paymentUrl: string | null; }
export interface PromotionSettingsDto { jobFreePerMonth: number; teamFreePerMonth: number; freeDays: number; plans: PromotionPlan[]; updatedAt: IsoDate | null; }
export interface AdminPromotionsDto { settings: PromotionSettingsDto; live: PromotionDto[]; recent: PromotionDto[]; paidThisMonth: number; freeThisMonth: number; }

// ---- the platform's invoices (e-Arşiv / e-Fatura)
export type InvoiceKind = 'EArsiv' | 'EFatura';
export type InvoiceStatus = 'Pending' | 'Issued' | 'Failed' | 'Cancelled';
export type InvoiceSource = 'PlatformFee' | 'Promotion' | 'Membership';
export interface InvoiceLine { description: string; quantity: number; unitPrice: number; vatRate: number; vatAmount: number; lineTotal: number; }
export interface InvoiceDto {
  id: Guid; documentNumber: string | null; kind: InvoiceKind; status: InvoiceStatus; source: InvoiceSource; description: string; buyerName: string;
  subtotal: number; vatTotal: number; total: number; currency: string; createdAt: string; issuedAt: string | null; lastError: string | null; attempts: number;
  nextAttemptAt: string | null; statementId: Guid | null; promotionId: Guid | null; organizationName: string | null;
}
export interface InvoicePartyDto { name: string; taxOffice: string | null; idScheme: 'VKN' | 'TCKN'; id: string; address: string; city: string; country: string; email: string | null; phone: string | null; }
export interface InvoiceDetailDto {
  invoice: InvoiceDto; ettn: Guid; seller: InvoicePartyDto; buyer: InvoicePartyDto; lines: InvoiceLine[]; recipientAlias: string | null;
  cancelledAt: string | null; cancelReason: string | null;
}
export interface AdminInvoicesDto {
  provider: string; providerEnabled: boolean; simulator: boolean; simulatorFailing: boolean; items: InvoiceDto[]; pending: number; failed: number;
  issuedThisMonth: number; vatThisMonth: number;
}

// ----------------------------------------------------------------------------- documents
export interface AnnualEarningsDto {
  year: number; years: number[]; name: string; email: string; earned: number; paidOut: number; notYetPaid: number;
  months: { month: number; earned: number; paidOut: number }[];
  customers: { organizationId: Guid; organizationName: string; earned: number; entries: number }[];
  payouts: PayoutDto[]; entries: EarningDto[]; issuer: IssuerDto; generatedAt: IsoDate;
}

// ----------------------------------------------------------------------------- notifications and saved job searches
export type NotificationGroup = 'jobs' | 'work' | 'teams' | 'payments' | 'reviews' | 'messages';
export const NOTIFICATION_GROUPS: readonly NotificationGroup[] = ['jobs', 'work', 'teams', 'payments', 'reviews', 'messages'];
export interface NotificationDto { id: Guid; kind: string; data: Record<string, string>; link: string; createdAt: IsoDate; readAt: IsoDate | null; }
export interface NotificationPageDto { items: NotificationDto[]; unreadCount: number; nextCursor: string | null; }
export interface InboxCountsDto { notifications: number; messages: number; }
export interface NotificationSettingsDto { emailEnabled: boolean; mutedGroups: NotificationGroup[]; emailAvailable: boolean; email: string; }
export interface JobAlertDto {
  id: Guid; category: JobCategory | null; sourcing: CycleSourcing | null; query: string | null; eligibleOnly: boolean; createdAt: IsoDate; lastNotifiedAt: IsoDate | null;
}
export interface CreateJobAlertRequest { category: JobCategory | null; sourcing: CycleSourcing | null; query: string | null; eligibleOnly: boolean; }

// ----------------------------------------------------------------------------- reviews and disputes
export type ReviewDirection = 'CustomerToTester' | 'TesterToCustomer';
export interface RatingSummaryDto { average: number | null; count: number; }
export interface ReviewDto {
  id: Guid; direction: ReviewDirection; stars: number; comment: string | null; authorName: string; authorTeam: string | null; organizationName: string;
  cycleId: Guid; cycleName: string; createdAt: IsoDate; updatedAt: IsoDate;
}
export interface ReviewListDto { summary: RatingSummaryDto; items: ReviewDto[]; }
export interface WriteReviewRequest { stars: number; comment: string | null; }
export interface CycleReviewTargetDto {
  participantId: Guid; name: string; userId: Guid; teamId: Guid | null; canReview: boolean; given: ReviewDto | null; received: ReviewDto | null;
}
export interface ParticipantReviewsDto { canReview: boolean; isAuthor: boolean; given: ReviewDto | null; received: ReviewDto | null; customerRating: RatingSummaryDto; }
export type DisputeKind = 'Run' | 'Bug';
export type DisputeStatus = 'Open' | 'Escalated' | 'ResolvedByCustomer' | 'Upheld' | 'Overturned' | 'Withdrawn';
export interface DisputeDto {
  id: Guid; kind: DisputeKind; status: DisputeStatus; executionId: Guid | null; bugId: Guid | null; subject: string; cycleId: Guid; cycleName: string;
  projectName: string; organizationId: Guid; organizationName: string; openedByUserId: Guid; openedByName: string; reason: string;
  originalDecision: string; decisionNote: string | null; customerResponse: string | null; customerRespondedAt: IsoDate | null;
  resolutionNote: string | null; resolvedByName: string | null; createdAt: IsoDate; resolvedAt: IsoDate | null;
}
export interface OpenDisputeRequest { executionId?: Guid | null; bugId?: Guid | null; reason: string; }

// ----------------------------------------------------------------------------- job questions and private messages
export interface JobQuestionDto {
  id: Guid; cycleId: Guid; question: string; askedByName: string; mine: boolean; answer: string | null; answeredByName: string | null;
  createdAt: IsoDate; answeredAt: IsoDate | null; isPublic: boolean;
}
export interface ConversationDto {
  id: Guid; cycleId: Guid; cycleName: string; participantId: Guid; title: string; subtitle: string; iAmCustomer: boolean; lastMessage: string | null;
  lastSenderName: string | null; lastMessageAt: IsoDate; unread: number;
}
export interface MessageDto { id: Guid; conversationId: Guid; senderUserId: Guid; senderName: string; fromCustomer: boolean; mine: boolean; body: string; createdAt: IsoDate; }
export interface MessagePageDto { conversation: ConversationDto; items: MessageDto[]; hasMore: boolean; }
export interface MessageEventDto { conversationId: Guid; message: MessageDto; }

// ----------------------------------------------------------------------------- integrations and the cycle report
export type IntegrationKind = 'Jira' | 'GitHub' | 'Slack';
export const INTEGRATION_KINDS: readonly IntegrationKind[] = ['Jira', 'GitHub', 'Slack'];
export type IntegrationEvent = 'BugReported' | 'BugConfirmed' | 'CyclePublished' | 'CycleClosed';
export const INTEGRATION_EVENTS: readonly IntegrationEvent[] = ['BugReported', 'BugConfirmed', 'CyclePublished', 'CycleClosed'];
/** Every kind is listed, set up or not. Reviewers get only kind/configured/enabled/target; the rest is for owners and admins. */
export interface IntegrationDto {
  kind: IntegrationKind; configured: boolean; enabled: boolean; baseUrl: string | null; account: string | null; target: string | null;
  issueType: string | null; secretHint: string | null; autoCreateIssues: boolean; events: IntegrationEvent[]; minSeverity: Severity;
  lastSuccessAt: IsoDate | null; lastFailureAt: IsoDate | null; lastError: string | null; updatedAt: IsoDate | null;
}
/** An empty `secret` keeps the stored one. */
export interface SaveIntegrationRequest {
  enabled: boolean; baseUrl: string | null; account: string | null; target: string | null; issueType: string | null; secret: string | null;
  autoCreateIssues: boolean; events: IntegrationEvent[]; minSeverity: Severity;
}
export interface IntegrationCheckDto { ok: boolean; message: string; }
/** Opened (externalKey), being opened (pending), or unknown (uncertain: the tracker may have opened it; error says what happened). */
export interface BugLinkDto {
  kind: IntegrationKind; externalKey: string | null; url: string | null; pending: boolean; uncertain: boolean; error: string | null; createdAt: IsoDate;
}

export interface ReportCaseDto {
  /** null: all exploratory sessions together. */
  key: string | null; title: string; priority: Priority | null; runs: number; passed: number; failed: number; blocked: number;
  accepted: number; rejected: number; bugs: number; averageConfidence: number | null;
}
export interface ReportSeverityDto { severity: Severity; total: number; open: number; confirmed: number; fixed: number; rejected: number; duplicate: number; }
export interface ReportBugDto {
  id: Guid; number: number; title: string; severity: Severity; status: BugStatus; reporterName: string; caseKey: string | null; createdAt: IsoDate;
  links: BugLinkDto[];
}
export interface ReportTesterDto { name: string; runs: number; accepted: number; rejected: number; bugs: number; confirmedBugs: number; averageConfidence: number | null; }
export interface ReportCountDto { label: string; count: number; }
export interface ReportCostDto {
  currency: string; testerEarnings: number; platformFee: number; total: number; paid: number; escrowPlanned: number; escrowHeld: number;
}
export interface CycleReportDto {
  cycleId: Guid; cycleName: string; projectName: string; organizationName: string; status: CycleStatus; platform: Platform;
  recordingMode: RecordingMode; sourcing: CycleSourcing; appVersion: string | null; targetUrl: string; activatedAt: IsoDate | null;
  closedAt: IsoDate | null; dueAt: IsoDate | null; progress: CycleProgress; outcomes: { passed: number; failed: number; blocked: number };
  bands: { trusted: number; check: number; suspicious: number }; averageConfidence: number | null; testerCount: number;
  cases: ReportCaseDto[]; bugsBySeverity: ReportSeverityDto[]; bugs: ReportBugDto[]; testers: ReportTesterDto[];
  /** Label "" = environment not given. */
  environments: ReportCountDto[];
  /** Label is a ReasonCode. */
  reasons: ReportCountDto[];
  /** Owners and admins, paid or budgeted cycles only. */
  cost: ReportCostDto | null; generatedAt: IsoDate; generatedBy: string;
}

// ----------------------------------------------------------------------------- platform admin console
export interface AdminDashboardDto {
  generatedAt: IsoDate;
  users: { total: number; testers: number; customers: number; new30: number; activeTesters30: number; suspended: number };
  work: {
    activeCycles: number; openJobs: number; runsSubmitted30: number; runsAccepted30: number; runsRejected30: number; pendingReviews: number;
    averageReviewHours30: number | null; bugsReported30: number; bugsConfirmed30: number;
  };
  money: {
    currency: string; volume30: number; volumeAllTime: number; fees30: number; feesAllTime: number; awaitingTransfer: number; awaitingPayout: number;
    walletAvailable: number; walletReserved: number; pendingDeposits: number; pendingRefunds: number;
  };
  disputes: { open: number; escalated: number; resolved30: number; averageResolutionHours30: number | null };
  openFraudClusters: number;
  weeks: { weekStart: IsoDate; runsSubmitted: number; volume: number; newUsers: number }[];
}
export type SignalKind = 'Device' | 'Ip' | 'Iban' | 'Phone';
export type FraudRisk = 'Medium' | 'High';
export type FraudDecision = 'Cleared' | 'Suspended';
export type AdminUserFilter = 'all' | 'testers' | 'customers' | 'linked' | 'suspended';
export interface AdminUserRowDto {
  id: Guid; displayName: string; email: string; createdAt: IsoDate; lastLoginAt: IsoDate | null; isTester: boolean; isPlatformAdmin: boolean;
  organizations: string[]; level: TesterLevel | null; runsSubmitted: number; earned: number; linkedAccounts: number; suspendedAt: IsoDate | null;
  suspendedReason: string | null;
}
export interface AdminUserPageDto { items: AdminUserRowDto[]; total: number; page: number; pageSize: number; }
export interface FraudUserDto {
  id: Guid; displayName: string; email: string; createdAt: IsoDate; lastLoginAt: IsoDate | null; isTester: boolean; organizations: string[];
  earned: number; suspendedAt: IsoDate | null;
}
export interface AdminSignalDto {
  kind: SignalKind; display: string; detail: string | null; count: number; firstSeenAt: IsoDate; lastSeenAt: IsoDate; sharedWith: FraudUserDto[];
}
export interface FraudReviewDto { decision: FraudDecision; note: string | null; reviewedByName: string; reviewedAt: IsoDate; userIds: Guid[]; }
export interface AdminUserDetailDto {
  user: AdminUserRowDto; suspendedByName: string | null; organizationRoles: MeOrganizationDto[]; communities: MeCommunityDto[];
  stats: TesterStatsDto | null; signals: AdminSignalDto[]; reviews: FraudReviewDto[];
  watermarkCode?: string | null;
}
export interface FraudLinkDto { kind: SignalKind; display: string; detail: string | null; userIds: Guid[]; since: IsoDate; }
export interface FraudJobDto { cycleId: Guid; cycleName: string; organizationName: string; userIds: Guid[]; }
export interface FraudClusterDto {
  key: string; risk: FraudRisk; users: FraudUserDto[]; links: FraudLinkDto[]; sharedJobs: FraudJobDto[]; selfDealing: FraudJobDto[];
  linkedSince: IsoDate; review: FraudReviewDto | null;
}
export interface ReviewClusterRequest { userIds: Guid[]; decision: FraudDecision; note: string | null; }

// ----------------------------------------------------------------------------- privacy notice and consents
export type ConsentKind = 'PrivacyNotice' | 'DeviceDetails';
export interface ConsentTextDto { kind: ConsentKind; version: string; required: boolean; title: string; body: string; }
export interface ConsentStatusDto {
  kind: ConsentKind; currentVersion: string; required: boolean; answeredVersion: string | null; granted: boolean | null; at: IsoDate | null; upToDate: boolean;
}
export interface ConsentHistoryDto { kind: ConsentKind; version: string; granted: boolean; at: IsoDate; ipAddress: string | null; }
export interface MyDeviceSnapshotDto {
  at: IsoDate; runTitle: string; detected: DetectedEnvironment | null; details: DeviceDetails | null; video: ScreenVideoInfo | null;
}
export interface MyPrivacyDto { consents: ConsentStatusDto[]; history: ConsentHistoryDto[]; latestDevice: MyDeviceSnapshotDto | null; runsWithDetails: number; }
export interface RecordConsentRequest { kind: ConsentKind; version: string; granted: boolean; }

// ---- error tracking (platform admins)
export type ErrorSource = 'Api' | 'Worker' | 'Web';
export type ErrorStatus = 'Open' | 'Resolved' | 'Ignored';
export interface ErrorReportDto {
  id: Guid; source: ErrorSource; kind: string; message: string; stack: string | null; path: string | null; release: string | null; userAgent: string | null;
  lastUserId: Guid | null; lastUserName: string | null; count: number; firstSeenAt: string; lastSeenAt: string; status: ErrorStatus; regressed: boolean;
  resolvedAt: string | null; resolvedByName: string | null;
}
export interface AdminErrorsDto { items: ErrorReportDto[]; open: number; regressed: number; seenLast24Hours: number; release: string; }

// ---- automation: Playwright tests from recorded runs
// ---------------------------------------------------------------- CI (test runs started from a pipeline)
export type CiFailOn = 'Critical' | 'Major' | 'Minor' | 'Any' | 'Never';
export type CiRunState = 'Running' | 'Passed' | 'Failed' | 'Incomplete' | 'Cancelled';
export interface CiTokenDto {
  id: Guid; name: string;
  /** The visible start of the key ("trci_AbC1234"). */
  prefix: string;
  templateCycleId: Guid | null; templateCycleName: string | null; failOn: CiFailOn;
  createdByName: string; createdAt: string; lastUsedAt: string | null;
}
export interface CreateCiTokenRequest { name: string; templateCycleId?: Guid | null; failOn?: CiFailOn }
/** `token` is shown only here, once. */
export interface CreatedCiTokenDto { token: string; key: CiTokenDto }
export interface CiRunDto {
  id: Guid; projectId: Guid; projectName: string; cycleId: Guid; cycleName: string; cycleStatus: CycleStatus;
  build: string | null; commit: string | null; branch: string | null; repository: string | null; pullRequest: number | null;
  commitUrl: string | null; pullRequestUrl: string | null; runUrl: string | null;
  failOn: CiFailOn; state: CiRunState; finished: boolean; description: string; blocking: number;
  progress: { cases: number; casesAccepted: number; casesFinished: number; openWork: number; awaitingReview: number; failedCases: number };
  bugs: { critical: number; major: number; minor: number; trivial: number };
  cycleUrl: string; reportUrl: string; tokenName: string | null; createdAt: string; finishedAt: string | null;
  reportedAt: string | null; commentedAt: string | null; reportError: string | null;
  /** Started when a coding assistant's change was approved (its title) or to test a fix it reported (the bug's number). */
  change?: string | null; bugNumber?: number | null;
}
export interface CycleCiDto {
  runId: Guid; build: string | null; commit: string | null; branch: string | null; pullRequest: number | null;
  commitUrl: string | null; pullRequestUrl: string | null; runUrl: string | null; state: CiRunState; description: string; failOn: CiFailOn;
}

export interface PlaywrightScriptDto {
  fileName: string; code: string; baseUrl: string; actions: number; assertions: number;
  /** Fields whose values the recording does not keep (filled in the script's VALUES). */
  values: string[];
  /** Environment variables the script reads (BASE_URL, TEST_USERNAME, TEST_PASSWORD). */
  environment: string[];
}

// ----------------------------------------------------------------------------- release quality and device coverage
export type ReleaseVerdict = 'NoData' | 'NotReady' | 'Risky' | 'Ready';
export type CoverageGroupCode = 'platform' | 'mobileOs' | 'androidVersion' | 'iosVersion' | 'desktopOs' | 'windowsVersion' | 'mobileBrowser' | 'desktopBrowser';
/** Valid bugs of a version (rejected and duplicate reports left out); open = new or confirmed. */
export interface ReleaseBugsDto { critical: number; major: number; minor: number; trivial: number; open: number; openCritical: number; openMajor: number; fixed: number; }
/** One app version across its published cycles; `key` is the version, "~" for cycles without one. `passRate` 0–1, `coverage` %. */
export interface ReleaseSummaryDto {
  key: string; version: string | null; cycles: number; testing: boolean; startedAt: string | null; lastActivityAt: string | null;
  cases: number; casesRun: number; runs: number; passed: number; failed: number; blocked: number; pendingReview: number; passRate: number | null;
  bugs: ReleaseBugsDto; coverage: number | null; verdict: ReleaseVerdict;
}
/** codes: allClear, openCritical, lowPassRate, passRate, openMajor, untestedCases, lowCoverage, openMinor, pendingReview, testing, noRuns */
export interface ReleaseFindingDto { code: string; value: string | null; positive: boolean; blocking: boolean; }
/** A test case (or, without an id, the exploratory sessions) with failures or bugs. */
export interface WeakAreaDto { testCaseId: Guid | null; key: string | null; title: string; runs: number; failed: number; blocked: number; bugs: number; passRate: number | null; }
export interface ReleaseCycleDto { id: Guid; name: string; status: CycleStatus; sourcing: CycleSourcing; activatedAt: string | null; runs: number; passRate: number | null; bugs: number; }
/** `percent`: share of its group's users; `reach`: share of the whole audience. */
export interface CoverageItemDto { key: string; label: string; percent: number; reach: number; runs: number; }
export interface CoverageGroupDto { code: CoverageGroupCode; covered: number; items: CoverageItemDto[]; }
export interface CoverageGapDto { group: CoverageGroupCode; key: string; label: string; reach: number; }
export interface MarketSourceDto { name: string; region: string; period: string; url: string; }
export interface DeviceCoverageDto { reach: number; groups: CoverageGroupDto[]; gaps: CoverageGapDto[]; other: ReportCountDto[]; unknown: number; source: MarketSourceDto; }
export interface ReleaseDetailDto {
  summary: ReleaseSummaryDto; findings: ReleaseFindingDto[]; previous: ReleaseSummaryDto | null; weakAreas: WeakAreaDto[]; cycles: ReleaseCycleDto[];
  coverage: DeviceCoverageDto;
}
export interface ProjectQualityDto { projectId: Guid; platform: Platform; versions: ReleaseSummaryDto[]; selected: ReleaseDetailDto | null; }

// ---------------------------------------------------------------- work rhythm (pauses across runs)
/** Submitted runs in the slice and their breaks (pauses at least the threshold long); coarse runs are screen-video runs. */
export interface RhythmTotalsDto {
  runs: number; coarseRuns: number; runsWithBreaks: number; breaks: number; breakMs: number; hiddenMs: number; totalMs: number; activeMs: number;
  averageActiveMs: number; pauses: number;
}
export interface RhythmTesterDto {
  userId: Guid; name: string; runs: number; runsWithBreaks: number; breaks: number; breakMs: number; totalMs: number; longestMs: number; hiddenMs: number;
}
export interface RhythmStepDto { order: number; title: string | null; breaks: number; breakMs: number; }
/** A case with breaks; no caseId means exploratory testing. */
export interface RhythmCaseDto {
  caseId: Guid | null; key: string | null; title: string; runs: number; runsWithBreaks: number; breaks: number; breakMs: number; topStep: RhythmStepDto | null;
}
/** Pauses with a length in [fromMs, toMs); the last bucket has no upper end. */
export interface RhythmBucketDto { code: string; fromMs: number; toMs: number | null; pauses: number; ms: number; }
/** A day (yyyy-MM-dd, Türkiye time) by the runs submitted on it. */
export interface RhythmDayDto { day: string; runs: number; runsWithBreaks: number; breaks: number; breakMs: number; }
export interface RhythmBreakDto {
  executionId: Guid; testerId: Guid; testerName: string; caseKey: string | null; caseTitle: string; stepOrder: number | null;
  from: IsoDate; to: IsoDate; durationMs: number; hiddenMs: number; fromKind: string; toKind: string; coarse: boolean;
}
export interface RhythmOptionDto { id: Guid; name: string; }
export interface WorkRhythmDto {
  from: string; to: string; thresholdSeconds: number; truncated: boolean; maxRuns: number;
  totals: RhythmTotalsDto; testerRows: RhythmTesterDto[]; cases: RhythmCaseDto[]; distribution: RhythmBucketDto[]; days: RhythmDayDto[];
  longest: RhythmBreakDto[]; cycles: RhythmOptionDto[]; testers: RhythmOptionDto[];
}

// ---------------------------------------------------------------- memberships (paid plans instead of commission)
export type MembershipAudience = 'Organization' | 'Tester';
export type BillingInterval = 'Monthly' | 'Yearly';
export type MembershipState = 'NotStarted' | 'Trial' | 'Active' | 'PastDue' | 'Cancelled' | 'Granted' | 'Expired';
/** A plan; the price includes VAT. yearlySaving (0–1) is set on a yearly plan cheaper than twelve monthly ones. */
export interface MembershipPlanDto {
  id: Guid; audience: MembershipAudience; name: string; description: string | null; features: string[]; interval: BillingInterval; price: number;
  currency: string; active: boolean; sortOrder: number; yearlySaving: number | null;
}
export interface MembershipPaymentDto {
  id: Guid; planName: string; interval: BillingInterval; amount: number; currency: string; periodStart: IsoDate; periodEnd: IsoDate; paidAt: IsoDate;
  invoiceId: Guid | null; invoiceNumber: string | null;
}
/** accessUntil: when access ends (trial end, end of the paid period, grace end or grant end). */
export interface MembershipStatusDto {
  audience: MembershipAudience; organizationId: Guid | null; state: MembershipState; entitled: boolean; trialEndsAt: IsoDate | null;
  accessUntil: IsoDate | null; daysLeft: number | null; plan: MembershipPlanDto | null; nextPlan: MembershipPlanDto | null; paidThrough: IsoDate | null;
  cancelledAt: IsoDate | null; lastPaymentError: string | null; grantNote: string | null; trialDays: number; required: boolean;
}
export interface MembershipBuyerDto { name: string; surname: string; identityNumberMasked: string | null; phone: string; address: string; city: string; }
/** blockers: 'notConfigured' (no card provider) or 'billing' (an organization's billing details are incomplete). */
export interface MembershipPageDto {
  status: MembershipStatusDto; plans: MembershipPlanDto[]; payments: MembershipPaymentDto[]; canManage: boolean; cardPaymentsEnabled: boolean;
  blockers: string[]; buyer: MembershipBuyerDto | null;
}
export interface MembershipBuyerRequest { name: string; surname: string; identityNumber: string; phone: string; address: string; city: string; }
export interface StartMembershipRequest { planId: Guid; buyer?: MembershipBuyerRequest | null; }
export interface MembershipCheckoutDto { checkoutUrl: string; }
export interface MeMembershipDto {
  audience: MembershipAudience; organizationId: Guid | null; state: MembershipState; entitled: boolean; daysLeft: number | null; accessUntil: IsoDate | null;
}
export interface MembershipSettingsDto { trialDays: number; graceDays: number; testerMembershipRequired: boolean; platformFeePercent: number; }
export interface UpsertMembershipPlanRequest {
  audience: MembershipAudience; name: string; description: string | null; features: string[]; interval: BillingInterval; price: number; active: boolean;
  sortOrder: number;
}
export interface AdminMembershipRowDto {
  membershipId: Guid | null; audience: MembershipAudience; subjectId: Guid; subjectName: string; email: string | null; state: MembershipState;
  planName: string | null; interval: BillingInterval | null; trialEndsAt: IsoDate | null; accessUntil: IsoDate | null; lastPaymentAt: IsoDate | null;
  lastPaymentError: string | null; grantNote: string | null; paid: number;
}
export interface AdminMembershipStatsDto { trial: number; active: number; pastDue: number; cancelled: number; granted: number; expired: number; monthlyRecurring: number; }
export interface AdminMembershipsDto {
  settings: MembershipSettingsDto; plans: MembershipPlanDto[]; rows: AdminMembershipRowDto[]; stats: AdminMembershipStatsDto; provider: string;
  simulator: boolean; simulatorFailRenewals: boolean;
}
export interface AdminMembershipUpdateRequest { trialEndsAt?: IsoDate | null; grantedUntil?: IsoDate | null; grantNote?: string | null; clearGrant?: boolean; }

// ---------------------------------------------------------------- coding assistant (TestRome plugin for Claude Code, MCP server for other tools)
/** mine: created by the person asking (managers see every key of the project, others their own). */
export interface AssistantTokenDto {
  id: Guid; name: string; prefix: string; createdByUserId: Guid; createdByName: string; createdAt: IsoDate; lastUsedAt: IsoDate | null; mine: boolean;
}
export interface CreateAssistantTokenRequest { name: string; }
/** The keys, and where the plugin is installed from (`/plugin marketplace add <pluginSource>`; empty when not set). */
export interface AssistantSetupDto { pluginSource: string; marketplace: string; plugin: string; tokens: AssistantTokenDto[]; }
/** token is shown only once. */
export interface CreatedAssistantTokenDto { token: string; key: AssistantTokenDto; }
export interface AffectedCaseDto { key: string; caseId: Guid | null; title: string | null; }
/** One batch of cases a coding assistant proposed for a change in the code. */
/** Change: cases for the change just made; Coverage: screens no case covered; Regression: cases guarding against bugs it fixed. */
export type SubmissionKind = 'Change' | 'Coverage' | 'Regression';
export type RevisionStatus = 'Pending' | 'Applied' | 'Rejected';
export interface BugRefDto { number: number; bugId: Guid | null; title: string | null; }
export interface RevisionStepDto {
  order: number; action: string; expectedResult: string; evidence: EvidenceRequirement; checkpointType: CheckpointType; checkpointValue: string | null;
}
export interface RevisionContentDto { title: string; preconditions: string | null; priority: Priority; steps: RevisionStepDto[]; }
/** A proposed update of an existing case: the case now and as proposed. stale: the case was edited after the proposal. */
export interface CaseRevisionDto {
  id: Guid; caseId: Guid; caseKey: string; reason: string; status: RevisionStatus; stale: boolean; note: string | null;
  current: RevisionContentDto; proposed: RevisionContentDto; createdAt: IsoDate; decidedAt: IsoDate | null;
}
export interface CaseSubmissionDto {
  id: Guid; title: string; summary: string | null; tool: string; branch: string | null; commit: string | null; repository: string | null;
  files: string[]; suggestedSuite: string | null; affected: AffectedCaseDto[]; createdByName: string; tokenName: string | null; createdAt: IsoDate;
  pending: number; approved: number; rejected: number; kind: SubmissionKind; pullRequest: number | null; bugs: BugRefDto[];
  /** Checkpoint events the assistant added to the app's code for these cases. */
  addedCheckpoints: string[]; revisions: CaseRevisionDto[]; pendingRevisions: number;
  /** The test cycle prepared from it when it was approved. */
  cycleId: Guid | null; cycleName: string | null;
}
/** A test cycle for the change: a copy of a template cycle with the change's cases, started at once or left as a draft. */
export interface ChangeCycleRequest { templateCycleId: Guid | null; start: boolean; }
export interface ApproveSubmissionRequest { suiteId?: Guid | null; newSuiteName?: string | null; cycle?: ChangeCycleRequest | null; }
export interface RejectSubmissionRequest { note: string; }
export interface ChangeCycleResultDto { cycleId: Guid; name: string; started: boolean; runId: Guid | null; cases: number; }
export interface SubmissionDecisionDto {
  approved: number; rejected: number; suiteId: Guid | null; applied: number; stale: number; cycle: ChangeCycleResultDto | null;
}
export interface CycleOptionDto { id: Guid; name: string; status: CycleStatus; sourcing: CycleSourcing; cases: number; createdAt: IsoDate; }
export interface ChangeCycleOptionsDto { templates: CycleOptionDto[]; defaultTemplateId: Guid | null; }
/** A fix a developer's coding assistant reported for a bug. */
export interface BugFixDto {
  id: Guid; tool: string; createdByName: string; createdAt: IsoDate; summary: string; branch: string | null; commit: string | null;
  repository: string | null; pullRequest: number | null; files: string[]; cycleId: Guid | null; cycleName: string | null;
}
